// apps/client/src/components/loans/LoanListTable.tsx
// Loan list table with dark theme styling

import React, { useState } from 'react';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { LoanListItem, LoanListSort } from '../../hooks/useLoanList';
import { LoanDetailModal } from './LoanDetailModal';
import { formatDollars } from '../../utils/formatters';

// Format date helper
const formatDate = (dateValue: any): string => {
  if (!dateValue) return 'N/A';
  try {
    const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);
    return date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return 'N/A';
  }
};

interface LoanListTableProps {
  loans: LoanListItem[];
  loading: boolean;
  sort: LoanListSort;
  onSortChange: (column: string) => void;
}

export function LoanListTable({ loans, loading, sort, onSortChange }: LoanListTableProps) {
  const [selectedLoan, setSelectedLoan] = useState<{
    loanId: string;
    customerName: string;
  } | null>(null);

  const columns: Array<{
    key: string;
    label: string;
    sortable: boolean;
    align?: 'left' | 'right';
    className?: string;
    format?: (value: any, loan: LoanListItem) => React.ReactNode;
  }> = [
    {
      key: 'loan_id',
      label: 'Loan ID',
      sortable: true,
      className: 'w-24',
      format: (value, loan) => (
        <button
          onClick={() => setSelectedLoan({ loanId: loan.loanId, customerName: loan.customerName })}
          className="text-blue-400 hover:text-blue-300 hover:underline font-medium"
        >
          {value}
        </button>
      ),
    },
    {
      key: 'customer_name',
      label: 'Customer Name',
      sortable: true,
      className: 'max-w-[200px]',
      format: (value) => (
        <span className="block truncate" title={value}>
          {value}
        </span>
      ),
    },
    {
      key: 'broker_display',
      label: 'Broker',
      sortable: true,
      className: 'max-w-[180px]',
      format: (value) => (
        <span className="block truncate" title={value}>
          {value}
        </span>
      ),
    },
    {
      key: 'lender',
      label: 'Lender',
      sortable: true,
    },
    {
      key: 'settlement_date',
      label: 'Settlement Date',
      sortable: true,
      format: (value) => formatDate(value),
    },
    {
      key: 'loan_amount_cents',
      label: 'Loan Amount',
      sortable: true,
      align: 'right',
      format: (value) => formatMoney(value),
    },
    {
      key: 'total_upfront_cents',
      label: 'Total Upfront',
      sortable: true,
      align: 'right',
      format: (value) => formatMoney(value),
    },
    {
      key: 'total_trail_cents',
      label: 'Total Trail',
      sortable: true,
      align: 'right',
      format: (value) => formatMoney(value),
    },
    {
      key: 'last_payment_month',
      label: 'Last Payment',
      sortable: true,
      format: (value) => formatMonth(value),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      format: (value, loan) => loan.trailStopped ? (
        <span
          className="inline-flex flex-col items-center px-2 py-0.5 rounded text-xs font-medium border bg-amber-900/30 text-amber-400 border-amber-500/30"
          title={trailStoppedTitle(loan)}
        >
          <span>Trail stopped</span>
          <span className="text-[10px] opacity-75">Last paid {formatMonth(loan.lastTrailPaidMonth ?? '')}</span>
          <span className="text-[10px] opacity-75">{loan.trailZeroMonths} months unpaid</span>
        </span>
      ) : (
        <span
          className={`inline-flex flex-col items-center px-2 py-0.5 rounded text-xs font-medium border ${
            value === 'active'
              ? 'bg-green-900/30 text-green-400 border-green-500/30'
              : 'bg-red-900/30 text-red-400 border-red-500/30'
          }`}
        >
          {value === 'active' ? (
            'Active'
          ) : (
            <>
              <span>Discharged</span>
              {loan.dischargeDate && (
                <span className="text-[10px] opacity-75">{formatMonth(loan.dischargeDate)}</span>
              )}
            </>
          )}
        </span>
      ),
    },
  ];

  const getValue = (loan: LoanListItem, key: string): any => {
    const mapping: Record<string, keyof LoanListItem> = {
      loan_id: 'loanId',
      customer_name: 'customerName',
      broker_display: 'brokerDisplay',
      lender: 'lender',
      settlement_date: 'settlementDate',
      loan_amount_cents: 'loanAmountCents',
      total_upfront_cents: 'totalUpfrontCents',
      total_trail_cents: 'totalTrailCents',
      last_payment_month: 'lastPaymentMonth',
      status: 'status',
      discharge_date: 'dischargeDate',
    };
    return loan[mapping[key]];
  };

  return (
    <>
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden">
        {/* Loading skeleton */}
        {loading && (
          <div className="animate-pulse p-4">
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-12 bg-gray-700 rounded"></div>
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {!loading && loans.length === 0 && (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-300">No loans found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Try adjusting your search or filter criteria.
            </p>
          </div>
        )}

        {/* Table */}
        {!loading && loans.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-900 border-b border-gray-700">
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      scope="col"
                      className={`px-6 py-4 text-xs font-semibold text-gray-300 uppercase tracking-wider ${
                        column.align === 'right' ? 'text-right' : 'text-left'
                      } ${column.className || ''}`}
                    >
                      {column.sortable ? (
                        <button
                          onClick={() => onSortChange(column.key)}
                          className="group inline-flex items-center gap-1.5 hover:text-white transition-colors"
                          title={`Click to sort by ${column.label}`}
                        >
                          {column.label}
                          <span className="flex-shrink-0">
                            {sort.column === column.key ? (
                              sort.direction === 'asc' ? (
                                <ChevronUpIcon className="h-4 w-4 text-blue-400" />
                              ) : (
                                <ChevronDownIcon className="h-4 w-4 text-blue-400" />
                              )
                            ) : (
                              // Always show stacked arrows to indicate sortability
                              <span className="flex flex-col -space-y-2.5 opacity-40 group-hover:opacity-70">
                                <ChevronUpIcon className="h-3 w-3" />
                                <ChevronDownIcon className="h-3 w-3" />
                              </span>
                            )}
                          </span>
                        </button>
                      ) : (
                        column.label
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {loans.map((loan) => (
                  <tr
                    key={loan.loanId}
                    className="hover:bg-gray-700 cursor-pointer transition-colors"
                    onClick={(e) => {
                      // Don't trigger if clicking on the loan ID link
                      if ((e.target as HTMLElement).tagName !== 'BUTTON') {
                        setSelectedLoan({ loanId: loan.loanId, customerName: loan.customerName });
                      }
                    }}
                  >
                    {columns.map((column) => {
                      const value = getValue(loan, column.key);
                      const formatted = column.format ? column.format(value, loan) : value;

                      return (
                        <td
                          key={column.key}
                          className={`px-6 py-4 text-sm text-gray-300 ${
                            column.align === 'right' ? 'text-right' : 'text-left'
                          } ${column.className || ''}`}
                        >
                          {formatted || 'N/A'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedLoan && (
        <LoanDetailModal
          loanId={selectedLoan.loanId}
          customerName={selectedLoan.customerName}
          onClose={() => setSelectedLoan(null)}
        />
      )}
    </>
  );
}

/**
 * Hover detail for the "Trail stopped" badge. The withheld figure is omitted
 * when it is 0. Callers who may not see trail never get trailStopped at all
 * (canSeeTrailStoppedState in getLoanList), so this only renders for those who can.
 */
export function trailStoppedTitle(loan: LoanListItem): string {
  const lines = [
    `Lender trail last paid ${formatMonth(loan.lastTrailPaidMonth ?? '')}, then nothing paid for ${loan.trailZeroMonths} months.`,
  ];
  if (loan.trailWithheldCents > 0) {
    lines.push(`Estimated trail withheld: ${formatDollars(loan.trailWithheldCents / 100)} (typical monthly trail x months unpaid).`);
  }
  if (loan.trailStoppedNote) {
    lines.push(`Lender note: ${loan.trailStoppedNote}`);
  }
  if (loan.trailStoppedSiblingLoanId) {
    lines.push(`Same client has loan ${loan.trailStoppedSiblingLoanId} still paying trail. This may be a split, offset or refinance rather than lost trail.`);
  }
  return lines.join('\n');
}

function formatMoney(cents: number): string {
  if (cents === 0 || cents === null || cents === undefined) {
    return '$0';
  }
  const amount = cents / 100;
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatMonth(monthString: string): string {
  if (!monthString) return 'N/A';
  try {
    // monthString is in YYYY-MM-DD or YYYY-MM format
    const date = new Date(monthString);
    return date.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });
  } catch {
    return monthString;
  }
}
